// ============================================
// AuthProvider: Supabase Auth をリアクティブに配信する Context
// SPEC-NEVER-STUCK-AUTH-AND-CHUNK-01 (D-230): 認証初期化が返らないとき永久に「認証中」で固まらないよう、
//   getSession() を 6 秒の時間切れと競わせ、いずれの経路でも必ず setSession に到達させる。
// SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268) PHASE_C + ADDENDUM_1 FIX_C7-C11: 認証のオフライン化(HOLE_3)。
//   HOLE_A 修正(FIX_C7): 圏外判定に navigator.onLine を使わない(弱電波/captive portal は onLine=true のまま失敗する)。
//     判定は「有効セッションを確認できたか」だけで行い、確認できず かつ キャッシュ職員があれば圏外継続に入る。
//   HOLE_B 修正(FIX_C10): 起動直後 SIGNED_OUT(session=null)が Promise.race より先着しても、キャッシュ職員を読んで
//     圏外継続の判定を行う(settled 早期 return で loadCachedStaff を飛ばさない)。
//   FIX_C8: purge とログイン遷移は「通信が生きて明確に session 無し」と返った場合(=online 再検証)に限る。
//     時間切れ/例外/初期化時の null は「不明」であって「無し」ではないため purge しない。
//   FIX_C9: 自発的ログアウトはキャッシュ職員を削除する(session.js logout)。以後 session=null+キャッシュ有り=不本意な失効。
//   FIX_C11: 認証キャッシュは store_meta ではなく専用ストア app_state に置く(appState.js)。
// ============================================
import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import { extractMeta } from './session'
import { Sentry } from '../sentry'
import { getCachedStaff, putCachedStaff, deleteCachedStaff } from '../localStore/appState'
import { traceAuth, TRACE } from '../authTrace' // D-838: 認証状態が変わった瞬間を残す
import { clearSessionActivity } from './sessionActivity' // D-838 FIX_B: 確定無効でも離席時刻を消す
import { setOfflineContinuation, registerOfflineRecovery } from './offlineGate' // D-304: 圏外継続フラグを React 外(supabase.js の守衛)へ公開 / D-348: 復帰経路

const AuthContext = createContext(null)

// A1(D-230): getSession は通常ローカル読取で即完結する。6 秒を超えたら Web Locks 取り残しと判断する。
// ★D-733: Login.jsx の亡霊判定も同じ時間切れを使う。**値を写さず輸入させる**ため export した
//   (2 か所に 6000 と書くと片方だけ変わってずれる / D-616)。★export を付けただけで振る舞いは 1 ビットも変えていない。
export const AUTH_INIT_TIMEOUT_MS = 6000

function nowMs() {
  return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()
}

// A3(D-230): 取り残しの掃除。localStorage の supabase 認証保存キー(sb- で始まり -auth-token で終わる)を削除する。
//   FIX_C8: online 再検証で「確定無効」と判明したときにだけ呼ぶ(圏外の一時失敗では絶対に呼ばない=C5)。
function purgeStaleAuthTokens() {
  let removed = 0
  try {
    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) keys.push(k)
    }
    for (const k of keys) {
      try { localStorage.removeItem(k); removed++ } catch { /* ignore */ }
    }
  } catch { /* private mode 等で localStorage 不可 */ }
  return removed
}

// A5(D-230): 初期化の時間切れ/失敗の計測。トークンや個人情報は絶対に送らない。
function reportAuthInit(kind, elapsedMs) {
  try {
    Sentry.captureMessage(`auth init ${kind}`, {
      level: 'warning',
      tags: { diag: 'D-230', kind },
      extra: { elapsedMs, hasLocks: typeof navigator !== 'undefined' && !!navigator.locks },
    })
  } catch { /* 計測失敗は認証を止めない */ }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined) // undefined = loading
  const [offlineStaff, setOfflineStaff] = useState(null) // C3: 圏外継続用の最後に確認できた職員情報
  // FIX_C12: 圏外継続に入っている間だけ定期再検証を回すためのフラグ(state 更新の非同期を待たず即時参照)。
  const offlineActiveRef = useRef(false)

  useEffect(() => {
    let disposed = false
    let settled = false
    const t0 = nowMs()

    const adoptValid = (s) => {
      if (disposed) return
      offlineActiveRef.current = false
      setOfflineContinuation(false) // D-304: 守衛に同値を反映(判定式は不変)
      setSession(s)
      setOfflineStaff(null)
      putCachedStaff(extractMeta(s)) // C6/FIX_C11: 有効セッションを app_state に保持
    }

    // FIX_C7/C8/C10: 有効セッションを確認できないとき。キャッシュ職員があれば圏外継続、無ければログイン。purge はしない。
    // SPEC-OFFLINE-GUARD-DEADLOCK-01 (D-348) FIX_1: 継続entry の判定は getCachedStaff()(app_state=IndexedDB、リロード生存)のみ。
    //   cached は adoptValid で書かれ、明示ログアウト(deleteCachedStaff)と revalidate 確定無効でのみ消える=「この端末で認証済みだった」の正しいマーカー。
    //   ※ React ref による事前分類は採らない: ref は page-load でリセットされ、圏外リロード(iOS 退避/SW activation)で
    //     継続に入れず AC3 を割る穴があるため。デッドロック解消の主体は FIX_2(書込のみ遮断)+FIX_3(読取成功で解除)。
    //   真の未認証(cached なし)= else でログイン=継続に入らない(AC2)。復帰ユーザの login 画面(cached あり)は一時継続でも
    //     FIX_2 で staff GET が通り(AC1)、その成功で FIX_3 が解除する(まず投げて成功で抜ける)。
    const applyNoValidSession = async () => {
      const cached = await getCachedStaff()
      if (disposed) return
      // ★D-838: ここが「未ログイン扱い」を決める場所。キャッシュの有無で行き先が変わる。
      traceAuth(TRACE.authState, 'no_valid_session', { hasSession: false, hasCached: !!cached })
      if (cached) { offlineActiveRef.current = true; setOfflineContinuation(true); setOfflineStaff(cached); setSession(null) } // 不本意な失効/圏外 → 継続
      else { offlineActiveRef.current = false; setOfflineContinuation(false); setOfflineStaff(null); setSession(null) }          // ログアウト済/未ログイン → ログイン
    }

    let timeoutId
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve({ kind: 'timeout' }), AUTH_INIT_TIMEOUT_MS)
    })
    const getSessionPromise = supabase.auth.getSession()
      .then(({ data }) => ({ kind: 'ok', session: data?.session ?? null }))
      .catch((err) => ({ kind: 'error', err }))

    Promise.race([getSessionPromise, timeoutPromise]).then(async (result) => {
      if (settled || disposed) return // 購読(onAuthStateChange)が先に決着していれば触らない
      settled = true
      clearTimeout(timeoutId)
      if (result.kind === 'ok' && result.session) { adoptValid(result.session); return }
      // FIX_C8: 初期化での timeout/error/ok-null は「不明 or 未確認」。purge せず、キャッシュがあれば圏外継続。
      if (result.kind === 'timeout') reportAuthInit('auth-init-timeout', Math.round(nowMs() - t0))
      else if (result.kind === 'error') reportAuthInit('auth-init-error', Math.round(nowMs() - t0))
      await applyNoValidSession()
    })

    // A4(D-230): 購読は常に最新のセッションを反映する。FIX_C10: SIGNED_OUT(null)先着でもキャッシュを読んで判定する。
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      // ★D-838: setSession 直後にどの順で何が届くかを見る(候補 1 の競合の有無)。
      traceAuth(TRACE.authState, String(_event || 'unknown').toLowerCase().replace(/[^a-z0-9_-]/g, ''), { hasSession: !!s })
      settled = true
      clearTimeout(timeoutId)
      if (s) adoptValid(s)
      else applyNoValidSession()
    })

    // C4/FIX_C8: 再検証。明確に session=null なら「確定無効」→ purge + キャッシュ削除 + ログイン要求。
    //   例外/通信失敗(不明)は触らない(誤ってログアウトさせない)。未送信ドラフトは IDB 保持で失わない。FIX_C8 の判定基準は不変。
    const revalidate = async () => {
      try {
        const { data } = await supabase.auth.getSession()
        const s = data?.session ?? null
        if (disposed) return
        if (s) { adoptValid(s); return }
        // 通信が生きた状態で「セッション無し」と明確に返った = 確定無効。ここでだけ D-230 の掃除を行う。
        // ★D-838: ログイン直後にこれが走ると、保存したばかりのトークンを消してしまう。
        //   起きているかどうかを実データで確かめる(起きていなければ候補から外せる)。
        traceAuth(TRACE.revalidatePurge, 'revalidate_no_session', { hasSession: false })
        purgeStaleAuthTokens()
        clearSessionActivity() // D-838 FIX_B: 確定無効もセッションの終わり。離席時刻を次のログインへ持ち越さない
        await deleteCachedStaff()
        if (disposed) return
        offlineActiveRef.current = false
        setOfflineContinuation(false) // D-304: 確定無効=継続解除
        setOfflineStaff(null)
        setSession(null)
      } catch { /* 不明(通信失敗/例外)は現状維持 */ }
    }

    // FIX_C12(NG3 対策): 再検証の発火点を online イベントだけに依存させない(iOS は online を取りこぼす)。
    //   ① online イベント、② visibilitychange の visible(前面復帰)、③ 圏外継続中は 60 秒間隔の再試行。
    //   これで「最初からオンラインなのに getSession が返らない(Web Locks 取り残し)」でも回復でき、HOLE_A は再発させない。
    const onOnline = () => { revalidate() }
    const onVisible = () => { if (typeof document !== 'undefined' && document.visibilityState === 'visible') revalidate() }
    const intervalId = setInterval(() => { if (offlineActiveRef.current) revalidate() }, 60000)
    if (typeof window !== 'undefined') window.addEventListener('online', onOnline)
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible)

    // D-348 FIX_3: 復帰を「通信の実績」に基づかせる。守衛が継続中に通した読み取りの 2xx 成功を検知したら revalidate を呼ぶ。
    //   getSession はローカル読取で通信しないことがあり復帰を取りこぼす(デッドロックの原因)。読み取り成功=通信が本当に戻った確証。
    //   新規の定期通信/health は足さない(AC7・電池要件): 既存の読み取りに相乗りするだけ。
    const unregisterRecovery = registerOfflineRecovery(() => { revalidate() })

    return () => {
      disposed = true
      clearTimeout(timeoutId)
      clearInterval(intervalId)
      subscription.unsubscribe()
      unregisterRecovery()
      if (typeof window !== 'undefined') window.removeEventListener('online', onOnline)
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return <AuthContext.Provider value={{ session, offlineStaff }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  const session = ctx?.session
  const offlineStaff = ctx?.offlineStaff ?? null
  const loading = session === undefined

  if (session) {
    // 通常(有効セッション)。返り値の形は従来と同一。
    return { session, loading: false, isLoggedIn: true, offline: false, ...extractMeta(session) }
  }
  if (!loading && offlineStaff) {
    // C2/C3: 圏外継続。権限判断はキャッシュ職員情報で行う(送信の瞬間だけオンライン必須)。
    return { session: null, loading: false, isLoggedIn: true, offline: true, ...offlineStaff }
  }
  // 未ログイン or loading。返り値の形は従来どおり(extractMeta(null) の空メタ)。
  return { session: null, loading, isLoggedIn: false, offline: false, ...extractMeta(null) }
}
